import { Controller } from '@hotwired/stimulus'
import TomSelect from 'tom-select'

const isSelected = (tomSelectValue, value) => {
  return Array.isArray(tomSelectValue)
    ? tomSelectValue.includes(value)
    : tomSelectValue === value
}

const defer = (callback) => () => setTimeout(callback, 0)

export default class extends Controller {
  static values = {
    placeholder: String,
    prompt: String,
    type: String,
    scrollParents: String,
    dropdownParent: {
      type: String,
      default: ''
    },
    autosuggestUrl: String,
    createRecordUrl: String,
    shouldSort: {
      type: Boolean,
      default: true
    },
    shouldSearch: {
      type: Boolean,
      default: true
    },
    shouldGroupSelected: {
      type: Boolean,
      default: false
    },
    createEvent: String,
    createWithModal: {
      type: Boolean,
      default: false
    },
    createRecordModalUrl: String,
    closeAfterSelect: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    showId: {
      type: Boolean,
      default: false
    },
    allowEmptyOption: {
      type: Boolean,
      default: false
    }
  }

  connect() {
    const options = this.getOptions()

    this.select = new TomSelect(this.element, options)

    if (this.disabledValue) {
      this.select.disable()
    }

    this.markDropdownContext()

    this.select.on('dropdown_open', defer(() => this.sortOptions()))
    this.select.on('load', () => this.sortOptions())
    this.select.on('item_remove', defer(() => this.sortOptions()))

    this.appendPrompt()

    if (this.shouldGroupSelectedValue && this.select.items.length > 0) {
      this.groupSelectedOptions()
    }

    if (this.hasScrollParentsValue) {
      this.scrollParentsValue.split(' ').forEach(scrollParentClass => {
        const scrollParent = document.querySelector(`.${scrollParentClass}`)
        scrollParent?.addEventListener('scroll', this.updateDropdownPosition.bind(this))
      })
    }

    // Listen for external clear events
    document.addEventListener('tom-select:clear', event => {
      if (event.detail.target === this.element.id) {
        this.select.clear()
      }
    })

    if (this.isSingleSelect()) {
      this.select.control.addEventListener('click', () => {
        setTimeout(() => this.updateCheckmarks(), 10)
      })
    }
  }

  updateCheckmarks() {
    const currentValue = this.select.getValue()
    this.select.dropdown_content.childNodes.forEach((node) => {
      const checkIcon = node.children[1]
      if (isSelected(currentValue, node.dataset.value)) {
        node.classList.add('selected')
        checkIcon?.classList.remove('hidden')
      } else {
        node.classList.remove('selected')
        checkIcon?.classList.add('hidden')
      }
    })
  }

  disconnect() {
    if (this.select) {
      this.select.destroy()
    }
  }

  getOptions() {
    switch (this.typeValue) {
      case 'single':
        return this.getSingleOptions();
      case 'single-create':
        return this.getSingleCreateOptions();
      case 'single-remote':
        return this.getSingleRemoteOptions();
      case 'single-create-remote':
        return this.getSingleCreateRemoteOptions();
      case 'multiple':
        return this.getMultipleOptions();
      case 'multiple-remote':
        return this.getMultipleRemoteOptions();
      case 'multiple-create':
        return this.getMultipleCreateOptions();
      case 'multiple-create-remote':
        return this.getMultipleCreateRemoteOptions();
      case 'multiple-create-remote-local':
        return this.getMultipleCreateRemoteLocalOptions();
      default:
        return {};
    }
  }

  getBaseOptions() {
    const options = {
      plugins: this.getPlugins(),
      labelField: 'label',
      valueField: 'value',
      dataAttr: 'data',
      allowEmptyOption: this.allowEmptyOptionValue,
      searchField: this.showIdValue ? ['label', 'value'] : 'label',
      hideSelected: false,
      placeholder: this.placeholderValue,
      hidePlaceholder: false,
      maxOptions: null,
      closeAfterSelect: this.closeAfterSelectValue
    }

    if (this.shouldSortValue) {
      options.sortField = 'label'
    }

    if (!this.shouldSearchValue) {
      options.controlInput = null
    }

    return options
  }

  getRemoteOptions() {
    return {
      preload: 'focus',
      load: this.loadData.bind(this)
    }
  }

  getSingleOptions() {
    const controller = this
    return {
      ...this.getBaseOptions(),
      maxItems: 1,
      render: {
        option: function (data, escape) {
          const isValueSelected = isSelected(this.getValue(), data.value)

          // option wrapper
          const divElement = document.createElement('div')
          divElement.classList.add('flex', 'items-center', 'gap-8')
          divElement.setAttribute('data-value', data.value)

          // text
          const spanElement = document.createElement('span')
          controller.appendLabel(spanElement, data)
          divElement.appendChild(spanElement)

          // check icon
          const iElement = document.createElement('i')
          iElement.classList.add('icon-tick', 'text-flameorange', 'text-xl')
          const iSpanElement = document.createElement('span')
          iSpanElement.classList.add('ml-auto')
          iSpanElement.classList.toggle('hidden', !isValueSelected)
          iSpanElement.appendChild(iElement)
          divElement.appendChild(iSpanElement)

          divElement.addEventListener('click', function (event) {
            event.stopPropagation()
            if (divElement.getAttribute('aria-disabled') === 'true') return

            const clickedValue = divElement.dataset.value

            isSelected(this.getValue(), clickedValue)
              ? this.removeItem(clickedValue)
              : this.addItem(clickedValue)
          }.bind(this))

          return divElement
        },
        loading: function () {
          return `<div class="loader-wrapper"><div class="loader"></div></div>`
        }
      },
      onChange: (value) => {
        this.setDropdownPosition()

        this.sortOptions()

        this.select.dropdown_content.childNodes.forEach((node) => {
          const checkIcon = node.children[1]
          if (isSelected(value, node.dataset.value)) {
            node.classList.add('selected')
            checkIcon?.classList.remove('hidden')
          } else {
            node.classList.remove('selected')
            checkIcon?.classList.add('hidden')
          }
        })

        this.handlePostSelectionBlur()
      },
      onType: () => {
        this.setDropdownPosition()
        this.sortOptions()
      },
      ...this.getManageDropdownPositionOptions()
    }
  }

  getSingleCreateOptions() {
    const options = {
      ...this.getSingleOptions(),
      create: true
    }

    if (this.createWithModalValue) {
      options.onOptionAdd = (value) => {
        this.openModal(value)
      }
    }
    return options
  }

  getSingleRemoteOptions() {
    return {
      ...this.getSingleOptions(),
      ...this.getRemoteOptions()
    }
  }

  getSingleCreateRemoteOptions() {
    const singleOptions = this.getSingleOptions()
    return {
      ...singleOptions,
      ...this.getRemoteOptions(),
      render: {
        ...singleOptions.render,
        option_create: function (data, escape) {
          const hasLabel = Object.values(this.options).some(option => option.label.toLowerCase() === data.input.toLowerCase())
          return hasLabel
            ? ''
            : `<div class="create">Add <span>${escape(data.input)}</span></div>`
        },
        no_results: () => ''
      },
      create: (input, callback) => {
        this.createRecord(input, callback)
      }
    }
  }

  getMultipleOptions() {
    const controller = this
    const options = {
      ...this.getBaseOptions(),
      render: {
        option: function (data, escape) {
          const isValueSelected = this.getValue().includes(data.value)

          // option wrapper
          const divElement = document.createElement('div')
          divElement.classList.add('flex', 'items-center', 'gap-10')
          divElement.setAttribute('data-value', data.value)

          // checkbox
          const checkboxElement = document.createElement('input')
          checkboxElement.type = 'checkbox'
          checkboxElement.classList.add('checkbox')
          checkboxElement.checked = isValueSelected
          divElement.appendChild(checkboxElement)

          // text
          const spanElement = document.createElement('span')
          controller.appendLabel(spanElement, data)
          divElement.appendChild(spanElement)

          divElement.addEventListener('click', function (event) {
            event.stopPropagation()
            const clickedValue = divElement.dataset.value
            this.getValue().includes(clickedValue) ? this.removeItem(clickedValue) : this.addItem(clickedValue)
          }.bind(this))

          return divElement
        },
        option_create: function (data, escape) {
          const hasLabel = Object.values(this.options).some(option => option.label.toLowerCase() === data.input.toLowerCase())
          return hasLabel
            ? ''
            : `<div class="create">Add <span>${escape(data.input)}</span></div>`
        },
        loading: function () {
          return `<div class="loader-wrapper"><div class="loader"></div></div>`
        }
      },
      onChange: (value) => {
        this.setDropdownPosition()
        this.sortOptions()
        this.checkOptionsSelection(value)

        this.togglePrompt()

        this.handlePostSelectionBlur()
      },
      onType: () => {
        this.setDropdownPosition()
        this.sortOptions()
        this.checkOptionsSelection(this.select.items)
      },
      onDelete: function (event) {
        if (event.key === 'Backspace' || event.key === 'Delete') {
          return false
        }
      },
      ...this.getManageDropdownPositionOptions()
    }

    if (this.shouldGroupSelectedValue) {
      options.render.item = (data, escape) => `<div class="item hidden!">${escape(data.label)}</div>`
      options.onItemAdd = () => this.groupSelectedOptions()
      options.onItemRemove = () => this.groupSelectedOptions()
    }

    return options
  }

  getMultipleRemoteOptions() {
    return {
      ...this.getRemoteOptions(),
      ...this.getMultipleOptions()
    }
  }

  getMultipleCreateOptions() {
    return {
      ...this.getMultipleOptions(),
      create: true
    }
  }

  getMultipleCreateRemoteOptions() {
    return {
      ...this.getMultipleOptions(),
      ...this.getRemoteOptions(),
      create: (input, callback) => {
        this.createRecord(input, callback)
      }
    }
  }

  getMultipleCreateRemoteLocalOptions() {
    return {
      ...this.getMultipleOptions(),
      ...this.getRemoteOptions(),
      create: true
    }
  }

  getManageDropdownPositionOptions() {
    const options = {}

    if (this.dropdownParentValue) {
      options.dropdownParent = this.dropdownParentValue
    }

    return {
      ...options,
      onDropdownOpen: () => {
        this.sortOptions()
        this.setDropdownPosition()
      },
      onDropdownClose: () => this.resetDropdownPosition(),
      onLoad: () => this.setDropdownPosition(),
      onFocus: () => this.setDropdownPosition()
    }
  }

  checkOptionsSelection(selectedOptions) {
    this.select.dropdown_content.querySelectorAll('[data-value]').forEach((node) => {
      const checkbox = node.querySelector('.checkbox')
      if (isSelected(selectedOptions, node.dataset.value)) {
        node.classList.add('selected')
        if (checkbox) {
          checkbox.checked = true
        }
      } else {
        node.classList.remove('selected')
        if (checkbox) {
          checkbox.checked = false
        }
      }
    })
  }

  setDropdownPosition() {
    if (!this.select.isOpen) return

    const inputRect = this.select.control.getBoundingClientRect()
    const dropdown = this.select.dropdown
    const dropdownContent = this.select.dropdown_content

    const spaceBelow = window.innerHeight - inputRect.bottom
    const spaceAbove = inputRect.top

    const DROPDOWN_PADDING = 10
    const DROPDOWN_TOP_GAP = 7
    const MAX_DROPDOWN_HEIGHT = 280
    const dropdownNonContentHeight = dropdown.offsetHeight - dropdownContent.offsetHeight

    if (!this.isDropdownPositionTop) {
      const openToTop = spaceBelow < dropdown.offsetHeight && spaceAbove > spaceBelow
      this.isDropdownPositionTop = openToTop
    }

    if (this.isDropdownPositionTop) {
      dropdown.classList.add('ts-dropdown--top')
      const availableHeight = Math.min(MAX_DROPDOWN_HEIGHT, spaceAbove - DROPDOWN_PADDING - dropdownNonContentHeight)
      dropdownContent.style.maxHeight = `${Math.max(availableHeight, 0)}px`
      dropdown.style.transform = `translateY(-${dropdown.offsetHeight + inputRect.height + DROPDOWN_TOP_GAP}px)`
      return
    }

    dropdown.classList.remove('ts-dropdown--top')
    dropdown.style.transform = ''
    const availableHeight = Math.min(MAX_DROPDOWN_HEIGHT, spaceBelow - DROPDOWN_PADDING - dropdownNonContentHeight)
    dropdownContent.style.maxHeight = `${Math.max(availableHeight, 0)}px`
  }

  updateDropdownPosition() {
    if (this.select.isOpen) {
      const dropdown = this.select.dropdown
      const inputRect = this.select.control.getBoundingClientRect()
      dropdown.style.top = `${inputRect.bottom}px`
      dropdown.style.left = `${inputRect.left}px`
    }
  }

  resetDropdownPosition() {
    this.select.dropdown.classList.remove('ts-dropdown--top')
    this.select.dropdown_content.style.maxHeight = ''
    this.isDropdownPositionTop = null
  }

  getPlugins() {
    return {
      ...(this.shouldSearchValue && { dropdown_input: {} }),
      ...(this.isMultipleSelect() && { remove_button: { label: '', className: 'icon-xmark p-4 2xl:p-8 text-8 2xl:text-10 cursor-pointer' } })
    }
  }

  loadData(query, callback) {
    if (!this.autosuggestUrlValue) return callback()

    const url = new URL(this.autosuggestUrlValue, window.location.origin)
    url.searchParams.set('search', query)

    fetch(url)
      .then(response => response.json())
      .then(data => callback(data))
      .catch(error => {
        console.error('Error loading data:', error)
        callback()
      })
  }

  sortOptions() {
    if (!this.shouldSortValue) {
      return
    }

    const dropdownContent = this.select.dropdown_content
    const options = Array.from(dropdownContent.childNodes)

    const selectValues = this.select.getValue()
    const selectedOptions = options.filter(option => isSelected(selectValues, option.dataset.value))
    const unselectedOptions = options.filter(option => !isSelected(selectValues, option.dataset.value))

    const sortedUnselectedOptions = unselectedOptions.sort((a, b) => {
      if (a.classList.contains('loader-wrapper') || b.classList.contains('loader-wrapper')) {
        return 0
      }

      const labelA = a.querySelector('span')?.textContent.toLowerCase()
      const labelB = b.querySelector('span')?.textContent.toLowerCase()
      return labelA?.localeCompare(labelB)
    })

    selectedOptions.forEach(option => dropdownContent.appendChild(option))
    sortedUnselectedOptions.forEach(option => dropdownContent.appendChild(option))
  }

  groupSelectedOptions() {
    const control = this.select.control
    const count = this.select.items.length

    const text = `${this.placeholderValue}: ${count} ${count > 1 ? 'options' : 'option'} selected`

    if (!count) {
      control.lastChild.remove()
      return
    }

    if (control.lastChild.dataset.hasOwnProperty('counter')) {
      control.lastChild.textContent = text
      return
    }

    const counter = `<div class="counter" data-counter>${text}</div>`
    control.insertAdjacentHTML('beforeend', counter)
  }

  isMultipleSelect() {
    return this.typeValue.includes('multiple')
  }

  createRecord(input, callback) {
    fetch(this.createRecordUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
      },
      body: JSON.stringify({ name: input })
    })
      .then(response => {
        if (!response.ok) {
          throw new Error("Failed to create record")
        }
        return response.json()
      })
      .then(data => {
        callback({ value: data.id, label: data.name })
        this.select.addItem(data.id)
      })
      .catch(error => {
        console.error("Error creating record:", error)
        callback(null)
      })
  }

  openModal(value) {
    const turboFrame = document.getElementById("modal_content")
    if (turboFrame) {
      const queryParams = new URLSearchParams({
        name: value,
        select_id: this.select.input.id,
      })
      turboFrame.src = `${this.createRecordModalUrlValue}?${queryParams.toString()}`
    }
    document.dispatchEvent(new CustomEvent("modal:show"))
  }

  handlePostSelectionBlur() {
    if (this.closeAfterSelectValue) {
      this.select.blur()
    }
  }

  appendPrompt() {
    if (!this.shouldShowPrompt()) return

    const control = this.select.control
    if (control.querySelector('.placeholder-text')) return

    const placeholder = document.createElement('span')
    placeholder.classList.add('placeholder-text', 'item')
    placeholder.textContent = this.promptValue
    control.appendChild(placeholder)
  }

  togglePrompt() {
    if (this.shouldShowPrompt()) {
      this.appendPrompt()
    } else {
      this.removePrompt()
    }
  }

  removePrompt() {
    const placeholderEl = this.select.control.querySelector('.placeholder-text')
    if (placeholderEl) {
      placeholderEl.remove()
    }
  }

  shouldShowPrompt() {
    return this.isMultipleSelect() &&  this.select.items.length === 0 &&  this.promptValue
  }

  isSingleSelect() {
    return this.typeValue.includes('single')
  }

  appendLabel(container, data) {
    if (this.showIdValue && data.value) {
      const labelSpan = document.createElement('span')
      labelSpan.textContent = data.label
      container.appendChild(labelSpan)

      const idSpan = document.createElement('span')
      idSpan.textContent = ` (ID: ${data.value})`
      idSpan.classList.add('text-xs', 'text-gray-500')
      container.appendChild(idSpan)
    } else {
      container.textContent = data.label
    }
  }

  markDropdownContext() {
    const isInModal = this.element.closest('.modal-backdrop') !== null
    const isInDrawer = this.element.closest('.drawer') !== null

    if (isInModal || isInDrawer) {
      this.select.dropdown.setAttribute('data-in-modal', 'true')
    }
  }
}
